/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Droplets, Thermometer, Hammer, Sun, Layers, Waves, Shield, Sparkles, PaintRoller, MessageCircle } from "lucide-react";
import { Reveal, LineReveal } from "@/components/Reveal";
import MarqueeBand from "@/components/MarqueeBand";
import Faq from "@/components/Faq";
import { IMG, sizedImg } from "@/lib/images";
import { api } from "@/lib/api";

const CHAPTERS = [
  { n: "01", t: "Ristrutturazioni chiavi in mano", d: "Gestiamo l'intero cantiere residenziale: dalla demolizione alle finiture, incluso il trattamento dell'umidità di risalita. Un unico interlocutore, tempi certi, qualità garantita.", links: [
    { to: "/finiture-e-rivestimenti", label: "risanamento dell'umidità di risalita" },
    { to: "/pareti-ventilate", label: "pareti ventilate" },
    { to: "/efficientamento-energetico", label: "efficientamento energetico" },
    { to: "/rivestimenti-piscine", label: "rivestimenti per piscine" },
    { to: "/rivestimenti-protettivi", label: "rivestimenti protettivi anticorrosivi" },
    { to: "/restauro-conservativo", label: "restauro conservativo" },
  ] },
  { n: "02", t: "Impermeabilizzazione in poliurea", d: "Membrana a spruzzo senza giunti, applicata in pochi secondi. Elasticità estrema e durabilità superiore a qualsiasi guaina tradizionale." },
  { n: "03", t: "Coibentazione in poliuretano", d: "Isolamento termico ad alte prestazioni. Riduce le dispersioni, abbatte i consumi e aumenta il valore dell'immobile." },
];

const STATS = [
  { v: 150, suffix: "+", l: "Cantieri completati" },
  { v: null, text: "Diversi", l: "Anni di esperienza" },
  { v: 100, suffix: "%", l: "Copertura Puglia" },
  { v: 10, suffix: "y", l: "Garanzia poliurea" },
];

const TRUST_ITEMS = [
  { code: "01", title: "Attestati di qualificazione", desc: "Riconoscimento delle competenze tecniche del team, verificate sul campo." },
  { code: "02", title: "Materiali certificati CE", desc: "Poliurea, poliuretano e sistemi selezionati secondo le più alte normative europee." },
  { code: "03", title: "Copertura Puglia e Italia", desc: "Cantieri gestiti su tutto il territorio nazionale, con base operativa in Puglia." },
  { code: "04", title: "Garanzia sui lavori", desc: "Ogni intervento è tracciato con schede tecniche e collaudi: qualità della posa garantita in ogni fase del cantiere." },
];

/**
 * Animated counter that counts from 0 to target when the element enters the viewport.
 */
function AnimatedNumber({ target, suffix = "", text = null, duration = 1600 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (text !== null) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const step = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(eased * target));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration, text]);

  return (
    <span ref={ref}>
      {text !== null ? text : `${value}${suffix}`}
    </span>
  );
}

const HOME_FAQ = [
  { q: "In quali zone operate?", a: "Abbiamo sede a Monopoli (BA) e operiamo in tutta la Puglia e sull'intero territorio nazionale." },
  { q: "Il sopralluogo e il preventivo sono gratuiti?", a: "Sì. Il sopralluogo e il preventivo sono gratuiti e senza impegno: valutiamo l'intervento e ti forniamo un'offerta chiara e dettagliata." },
  { q: "Offrite garanzie sui lavori?", a: "Sì. Utilizziamo materiali certificati e rilasciamo garanzie sugli interventi, con garanzia fino a 10 anni sull'impermeabilizzazione in poliurea." },
  { q: "In quanto tempo rispondete a una richiesta?", a: "Ti ricontattiamo generalmente entro 24-48 ore dalla richiesta per organizzare il sopralluogo." },
];

function ServiceCard({ to, icon: Icon, code, title, desc, image }) {
  return (
    <Link
      to={to}
      data-testid={`service-card-${code}`}
      className="group relative h-full overflow-hidden border border-border bg-card flex flex-col"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={image} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-foreground/25 group-hover:bg-foreground/10 transition-colors duration-500" />
        <span className="absolute top-4 left-4 font-mono text-xs bg-background px-2 py-1">{code}</span>
      </div>
      <div className="p-7 flex flex-col flex-1">
        <Icon className="w-7 h-7 text-primary mb-4" />
        <h3 className="font-head font-bold text-2xl tracking-tight mb-3">{title}</h3>
        <p className="text-muted-foreground leading-relaxed flex-1">{desc}</p>
        <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground group-hover:text-primary transition-colors">
          Scopri <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden flex items-end">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -top-[10%] h-[120%]">
          <img src={IMG.hero} alt="Cantiere Greenhouse Solutions" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-foreground/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-foreground/30" />
        </motion.div>

        <div className="relative w-full max-w-[1500px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-widest text-background/80 mb-6 flex items-center gap-3"
          >
            <span className="w-2 h-2 bg-primary" /> Greenhouse Solutions SPA — Puglia · Italia
          </motion.div>
          <h1 className="font-head font-black text-background text-[1.8rem] min-[380px]:text-[2.1rem] sm:text-[2.8rem] md:text-[3.5rem] lg:text-[4.75rem] xl:text-[6.25rem] 2xl:text-[8rem] leading-[0.92] tracking-tightest">
            <LineReveal lines={["Ristrutturazioni.", "Impermeabilizzazione.", "Coibentazione."]} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8"
          >
            <p className="max-w-xl text-background/80 text-lg leading-relaxed">
              Tecnologie ad alte prestazioni per l'edilizia residenziale. Poliurea e poliuretano
              applicati a regola d'arte, in tutta la Puglia e in tutta Italia.
            </p>
            <Link
              to="/preventivo"
              data-testid="hero-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-8 py-5 hover:bg-background hover:text-foreground transition-colors duration-200"
            >
              Richiedi Preventivo <ArrowUpRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <MarqueeBand />

      {/* MANIFESTO */}
      <section className="py-24 md:py-32 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">// Chi Siamo</p>
                <h2 className="font-head font-black text-4xl md:text-5xl tracking-tightest leading-[0.95]">
                  Un partner tecnico per ogni cantiere.
                </h2>
                <p className="mt-6 text-muted-foreground leading-relaxed">
                  Greenhouse Solutions SPA nasce dalla volontà di portare nell'edilizia residenziale
                  materiali e metodi normalmente riservati all'industria. Precisione, controllo e
                  risultati misurabili.
                </p>
                <Link to="/chi-siamo" data-testid="home-about-link" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-b-2 border-primary pb-1 hover:gap-3 transition-all">
                  Scopri l'azienda <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.1}>
                  <div className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 py-8 border-t border-border last:border-b">
                    <span className="font-mono text-4xl md:text-6xl text-primary leading-none">{c.n}</span>
                    <div>
                      <h3 className="font-head font-bold text-2xl md:text-3xl tracking-tight mb-2">{c.t}</h3>
                      <p className="text-muted-foreground leading-relaxed">{c.d}</p>
                      {c.links && (
                        <p className="text-muted-foreground leading-relaxed mt-3">
                          Integriamo il cantiere con soluzioni specializzate come{" "}
                          {c.links.map((lnk, idx) => (
                            <span key={lnk.to}>
                              <Link
                                to={lnk.to}
                                data-testid={`chapter-link-${lnk.to.replace(/\//g, "")}`}
                                className="text-foreground font-semibold underline decoration-primary decoration-2 underline-offset-4 hover:text-primary transition-colors"
                              >
                                {lnk.label}
                              </Link>
                              {idx < c.links.length - 2 ? ", " : idx === c.links.length - 2 ? " e " : "."}
                            </span>
                          ))}
                        </p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-secondary/40 border-y border-border">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <h2 className="font-head font-black text-4xl md:text-6xl tracking-tightest leading-[0.95] max-w-2xl">
                Le nostre <span className="text-primary">specializzazioni</span>
              </h2>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground max-w-xs">
                Servizi tecnici certificati per ogni fase del cantiere residenziale.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            <Reveal className="h-full"><ServiceCard to="/ristrutturazioni-chiavi-in-mano" icon={Hammer} code="S.01" title="Ristrutturazioni Chiavi in Mano" desc="Gestione completa del cantiere residenziale, dalla progettazione alla consegna." image={IMG.villa} /></Reveal>
            <Reveal className="h-full" delay={0.1}><ServiceCard to="/impermeabilizzazione-poliurea" icon={Droplets} code="S.02" title="Impermeabilizzazione Poliurea" desc="Membrana continua a spruzzo, impermeabile in pochi secondi, resistente e senza giunti." image={IMG.waterproofing} /></Reveal>
            <Reveal className="h-full" delay={0.2}><ServiceCard to="/coibentazione-poliuretano" icon={Thermometer} code="S.03" title="Coibentazione Poliuretano" desc="Isolamento termico a spruzzo che elimina i ponti termici e riduce i consumi energetici." image={IMG.insulation} /></Reveal>
            <Reveal className="h-full"><ServiceCard to="/efficientamento-energetico" icon={Sun} code="S.04" title="Efficientamento Energetico" desc="Impianti fotovoltaici ad alto rendimento per produrre energia pulita e abbattere i costi." image={IMG.solar} /></Reveal>
            <Reveal className="h-full" delay={0.1}><ServiceCard to="/pareti-ventilate" icon={Layers} code="S.05" title="Pareti Ventilate" desc="Facciate ventilate che isolano, proteggono l'edificio ed esaltano l'estetica." image={IMG.facade} /></Reveal>
            <Reveal className="h-full" delay={0.2}><ServiceCard to="/rivestimenti-piscine" icon={Waves} code="S.06" title="Rivestimenti Piscine" desc="Innovativo rivestimento polimerico continuo per piscine nuove e da ripristinare." image={IMG.pool} /></Reveal>
            <Reveal className="h-full"><ServiceCard to="/rivestimenti-protettivi" icon={Shield} code="S.07" title="Rivestimenti Protettivi Anticorrosivi" desc="Spruzzatura a fiamma di polveri termoplastiche per protezione conservativa e anticorrosione." image={IMG.steel} /></Reveal>
            <Reveal className="h-full" delay={0.1}><ServiceCard to="/restauro-conservativo" icon={Sparkles} code="S.08" title="Restauro Conservativo" desc="Microaeroabrasione selettiva, pulitura e trattamento protettivo delle superfici." image={IMG.stone} /></Reveal>
            <Reveal className="h-full" delay={0.2}><ServiceCard to="/finiture-e-rivestimenti" icon={PaintRoller} code="S.09" title="Finiture e Rivestimenti" desc="Pavimentazioni, intonaci, tinteggiature e finiture di pregio, incluso il risanamento dell'umidità di risalita." image={IMG.interior} /></Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-foreground text-background" data-testid="home-stats">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08}>
              <div className="border-l border-background/20 pl-5">
                <div className="font-head font-black text-4xl sm:text-5xl md:text-6xl tracking-tightest text-primary" data-testid={`stat-value-${i}`}>
                  <AnimatedNumber target={s.v} suffix={s.suffix} text={s.text} />
                </div>
                <div className="mt-2 font-mono text-xs uppercase tracking-widest text-background/60">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST RIBBON — credibility markers */}
      <section className="py-24 md:py-28 bg-background border-b border-border" data-testid="home-trust">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">// I nostri standard</p>
                <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest max-w-2xl leading-[0.95]">
                  Competenze verificate,<br />garanzie reali.
                </h2>
              </div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground max-w-xs md:text-right">
                Ogni intervento è sostenuto da competenze certificate, materiali di livello europeo e garanzie sui lavori.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {TRUST_ITEMS.map((t, i) => (
              <Reveal key={t.code} delay={i * 0.08}>
                <div
                  data-testid={`trust-item-${i}`}
                  className="p-6 md:p-8 h-full flex flex-col bg-primary text-primary-foreground md:hover:bg-foreground md:hover:text-background transition-colors"
                >
                  <h3 className="font-head font-black text-xl md:text-2xl tracking-tightest leading-tight mb-3">
                    {t.title}
                  </h3>
                  <p className="font-mono text-xs uppercase tracking-widest text-primary-foreground/80 leading-relaxed">
                    {t.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      {projects.length > 0 && (
        <section className="py-24 md:py-32 bg-background">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <Reveal>
              <div className="flex items-end justify-between mb-14">
                <h2 className="font-head font-black text-4xl md:text-6xl tracking-tightest">Progetti recenti</h2>
                <Link to="/progetti" data-testid="home-projects-link" className="hidden md:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:text-primary transition-colors">
                  Tutti i progetti <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {projects.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <Link to={`/progetti/${p.slug || p.id}`} data-testid={`home-project-link-${p.slug || p.id}`} className="group block border border-border bg-card h-full">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <span className="font-mono text-xs uppercase tracking-widest text-primary">{p.category}</span>
                      <h3 className="font-head font-bold text-xl tracking-tight mt-2 group-hover:text-primary transition-colors">{p.title}</h3>
                      <p className="font-mono text-xs text-muted-foreground mt-2">{p.location} — {p.year}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ BREVE + WHATSAPP */}
      <Faq items={HOME_FAQ} />
      <section data-testid="home-faq-whatsapp" className="pb-24 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="border border-border bg-secondary/40 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-head font-bold text-xl md:text-2xl tracking-tight">Hai altre domande?</h3>
              <p className="text-muted-foreground mt-1">Scrivici su WhatsApp: ti rispondiamo subito.</p>
            </div>
            <a
              href="https://wa.me/393519261622"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-faq-whatsapp-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-[#25D366] text-white font-mono text-sm uppercase tracking-widest px-8 py-5 hover:bg-[#128C7E] transition-colors"
            >
              <MessageCircle className="w-5 h-5" /> Scrivici su WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-40 bg-primary overflow-hidden">
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary-foreground/70 mb-6">// Iniziamo</p>
            <h2 className="font-head font-black text-primary-foreground text-4xl sm:text-6xl lg:text-8xl tracking-tightest leading-[0.9]">
              Hai un cantiere?<br />Parliamone.
            </h2>
            <Link
              to="/preventivo"
              data-testid="cta-preventivo-bottom"
              className="mt-10 inline-flex items-center gap-2 bg-foreground text-background font-mono text-sm uppercase tracking-widest px-10 py-6 hover:bg-background hover:text-foreground transition-colors duration-200"
            >
              Richiedi un preventivo gratuito <ArrowUpRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
